import { PAYWAY_GROUPS_TYPES } from '@/base/const/payway.const'
import { REPORT_LEVEL, reportMessage } from '@/base/utils/errorReporting'

const formatFee = (value) => Number(value).toFixed(2).replace('.', ',')

/**
 * Transakcje, dla których brak prowizji w danych potwierdzenia został już zgłoszony.
 *
 * `getPaymentFeeAsSingleValue` jest wołane wprost z szablonu (kilka razy na render), więc bez tego
 * jedna odpowiedź API dawałaby serię identycznych zdarzeń. Klucz budujemy z `returnUrl`, bo przy
 * braku `transaction` nie ma w danych ani `orderId`, ani `transRemoteID`.
 */
const reportedMissingConfirmationFee = new Set()

/**
 * Metoda zgłaszająca dane potwierdzenia bez prowizji.
 *
 * Po dołożeniu strażnika ten stan przestał rzucać, czyli **przestał być widoczny w Sentry sam
 * z siebie** - a oznacza potwierdzenie, na którym płatnik nie widzi opłaty, choć serwis ma włączony
 * model płatnika (`showFrontFees`). Lista kluczy odpowiedzi mówi, czy backend przysłał inny kształt,
 * czy urwaną odpowiedź - bez niej nie da się napisać zgłoszenia.
 */
function reportMissingConfirmationFee(confirmationData) {
  const key = String(confirmationData?.returnUrl ?? confirmationData?.state ?? null)
  if (reportedMissingConfirmationFee.has(key)) {
    return
  }
  reportedMissingConfirmationFee.add(key)
  reportMessage('Potwierdzenie: dane bez prowizji', {
    level: REPORT_LEVEL.warning,
    tags: { flow: 'payment-fee' },
    extra: {
      state: confirmationData?.state ?? null,
      serviceType: confirmationData?.serviceType ?? null,
      czyTransaction: !!confirmationData?.transaction,
      kluczeOdpowiedzi: confirmationData ? Object.keys(confirmationData) : null,
    },
  })
}

export const isPaywallView = (paymentData) => paymentData !== null

export const isConfirmationView = (confirmationData) => confirmationData !== null

export const isPaymentFeeActive = ({ paymentData = null, confirmationData = null } = {}) => {
  const data = isPaywallView(paymentData) ? paymentData : confirmationData
  // sprawdzamy flagę - czy wyświetlać prowizję - bez tego mamy model merchanta a nie płatnika
  // weryfikacja SIDa (PAYMENT_FEE_SID_CONFIG) odbywa się wyłącznie w store payment
  if (!data || !data?.visibilityOptions) return false
  return data.visibilityOptions.showFrontFees
}

export const isPaymentFeeLegacyMode = (paymentData = null) => {
  return paymentData != null && paymentData.customerFee !== undefined
}

export const getPaymentFeeList = (paymentData = null) => {
  return (paymentData?.paywayGroups || [])
    .flatMap((group) => group.payways || [])
    .map((payway) => Number(payway.customerFee))
}

export const isPaymentFeeDifferent = ({ paymentData = null, confirmationData = null } = {}) => {
  if (isConfirmationView(confirmationData)) {
    return false
  }

  return new Set(getPaymentFeeList(paymentData)).size > 1
}

export const getPaymentFeeAsSingleValue = ({ paymentData = null, confirmationData = null } = {}) => {
  if (isConfirmationView(confirmationData)) {
    // `?.` stało tylko przy `confirmationData` i nie chroniło `transaction` - a `isConfirmationView`
    // sprawdza wyłącznie `!== null`, więc każda odpowiedź `/confirmation` bez `transaction` rzucała
    // (`reading 'customerFee'` w Blinku, `t.transaction is undefined` w WebKicie - stąd osobne grupy
    // w Sentry o jednakowej przyczynie). Zwracamy `null`, bo `formatFee(undefined)` dałoby „NaN".
    const customerFee = confirmationData?.transaction?.customerFee
    if (customerFee === undefined || customerFee === null) {
      reportMissingConfirmationFee(confirmationData)
      return null
    }
    return formatFee(customerFee)
  }

  if (isPaymentFeeLegacyMode(paymentData)) {
    return formatFee(paymentData.customerFee)
  }

  if (!isPaymentFeeDifferent({ paymentData, confirmationData })) {
    const feeList = getPaymentFeeList(paymentData)
    return Number(feeList[0]) || 0
  }

  return null
}

export const getPaymentFeeForGroup = (group, currency = 'PLN') => {
  const feeList = group.payways.map((payway) => Number(payway.customerFee))
  if (feeList.length === 0) return formatFee(0) + ' ' + currency

  const min = Math.min(...feeList)
  const max = Math.max(...feeList)
  return min === max ? formatFee(min) + ' ' + currency : `${formatFee(min)} - ${formatFee(max)} ${currency}`
}

export const getPaymentFeeForPaywayAsNumber = (payway) => Number(payway.customerFee)

export const getPaymentFeeForActivePaywayAsNumber = ({ paymentData = null, paywayID = null } = {}) => {
  if (!paywayID) return 0

  const payway = (paymentData?.paywayGroups || [])
    .flatMap((group) => group.payways || [])
    .find((payway) => payway.id === paywayID)

  return payway ? Number(payway.customerFee) : 0
}

export const getPaymentFeeForPayway = (payway, currency = 'PLN') => {
  return formatFee(payway.customerFee) + ' ' + currency
}

export const sortPaymentGroups = (groups = []) => {
  const idx = groups.findIndex((group) => group.type === PAYWAY_GROUPS_TYPES.blik)
  if (idx === -1) return [...groups]
  return [...groups.slice(0, idx), ...groups.slice(idx + 1), groups[idx]]
}
